import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// Helper to decode JWT and get exp (expiration) in seconds
function getTokenExp(token) {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp || null;
  } catch {
    return null;
  }
}

const apiUrl = process.env.REACT_APP_API_URL || "";

export default function SessionTimeoutBox({ minWarning = 5 }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [countdown, setCountdown] = useState(null); // seconds left
  const [show, setShow] = useState(false);
  const [extending, setExtending] = useState(false);
  const [tokenVersion, setTokenVersion] = useState(0); // bump when token updates
  const timerRef = useRef();
  // Extend session handler
  async function handleExtendSession() {
    setExtending(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${apiUrl.replace(/\/$/, "")}/refresh-token`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      let data = null;
      try {
        data = await res.json();
      } catch {}
      if (res.ok && data && data.token) {
        localStorage.setItem("token", data.token);
        setTokenVersion((v) => v + 1);
        // Recalculate expiration and restart timer immediately
        const newExp = getTokenExp(data.token);
        if (newExp) {
          const now = Math.floor(Date.now() / 1000);
          const left = newExp - now;
          // If still within warning window, keep showing with updated time; otherwise hide
          if (left <= minWarning * 60) {
            setShow(true);
            setCountdown(left);
          } else {
            setShow(false);
            setCountdown(null);
          }
        } else {
          setShow(false);
          setCountdown(null);
        }
      } else {
        // If refresh fails, log out
        localStorage.removeItem("token");
        navigate("/login");
      }
    } catch {
      localStorage.removeItem("token");
      navigate("/login");
    } finally {
      setExtending(false);
    }
  }

  useEffect(() => {
    // Get token from localStorage
    const token = localStorage.getItem("token");
    const exp = getTokenExp(token); // seconds since epoch
    if (!exp) {
      setShow(false);
      setCountdown(null);
      return;
    }
    const now = Math.floor(Date.now() / 1000);
    const secondsLeft = exp - now;
    if (secondsLeft <= 0) {
      // Already expired
      localStorage.removeItem("token");
      setShow(false);
      setCountdown(null);
      navigate("/login");
      return;
    }
    // Show box if less than minWarning minutes left
    if (secondsLeft <= minWarning * 60) {
      setShow(true);
      setCountdown(secondsLeft);
    } else {
      setShow(false);
      setCountdown(null);
    }
    // Set up interval to update countdown every second
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      const now2 = Math.floor(Date.now() / 1000);
      const left = exp - now2;
      if (left <= 0) {
        clearInterval(timerRef.current);
        localStorage.removeItem("token");
        setShow(false);
        setCountdown(null);
        navigate("/login");
      } else if (left <= minWarning * 60) {
        setShow(true);
        setCountdown(left);
      } else {
        setShow(false);
        setCountdown(null);
      }
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line
  }, [location.pathname, tokenVersion]);

  if (!show || countdown == null) return null;
  const min = Math.floor(countdown / 60);
  const sec = countdown % 60;
  return (
    <div
      style={{
        position: "fixed",
        top: 16,
        right: 16,
        background: "#fff3cd",
        color: "#856404",
        border: "1px solid #ffeeba",
        borderRadius: 8,
        padding: "12px 20px",
        zIndex: 9999,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        fontWeight: 500,
        fontSize: 15,
        display: "flex",
        alignItems: "center",
        gap: 16,
      }}
    >
      <span>
        Will log out in {min}:{sec.toString().padStart(2, "0")} minutes
      </span>
      <button
        onClick={handleExtendSession}
        disabled={extending}
        style={{
          marginLeft: 12,
          padding: "6px 14px",
          borderRadius: 5,
          border: "1px solid #ffeeba",
          background: extending ? "#ffeeba" : "#fff",
          color: "#856404",
          fontWeight: 600,
          cursor: extending ? "not-allowed" : "pointer",
          fontSize: 14,
        }}
      >
        {extending ? "Extending..." : "Extend Session"}
      </button>
    </div>
  );
}
